import type { RouteObject } from "react-router-dom";

import { Spinner } from "@/components/ui/v2/spinner";

import { CompanyPayrolls } from "@/pages/(home)/(company)/payrolls";
import { PayrollSummary } from "@/pages/(home)/(company)/payrolls/summary";
import { CompanyDisputes } from "@/pages/(home)/(company)/payrolls/disputes";
import { CompanyDisputeDetail } from "@/pages/(home)/(company)/payrolls/disputes/detail";
import { CompanyPaySlips } from "@/pages/(home)/(company)/payrolls/pay-slips";
import { CompanyPaymentFiles } from "@/pages/(home)/(company)/payrolls/payment-files";
import { CompanyFinalSettlements } from "@/pages/(home)/(company)/payrolls/final-settlements";
import { FinalSettlementDetail } from "@/pages/(home)/(company)/payrolls/final-settlements/detail";
import { GeneratePayroll } from "@/pages/(home)/(company)/payrolls/generate-payroll";
import { PayrollRun } from "@/pages/(home)/(company)/payrolls/run";
import { EditPayrollRunItem } from "@/pages/(home)/(company)/payrolls/run/edit-item";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import {
  CompanyPayrollLayout,
  CompanyPayrollSettingsPage,
} from "@/utils/lazyLoadComponents";

export enum COMPANY_PAYROLL_PATH_SEGMENTS {
  ROOT = "payroll",
  SUMMARY = "summary",
  DISPUTES = "disputes",
  PAY_SLIPS = "pay-slips",
  PAYMENT_FILES = "payment-files",
  FINAL_SETTLEMENTS = "final-settlements",
  GENERATE = "generate",
  RUN = "run",
  // Module settings (moved out of Company Settings — also absorbs the old
  // Compensation Settings tab)
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
}

export const companyPayrollRoutes: RouteObject = {
  path: COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
  HydrateFallback: Spinner,
  handle: { breadcrumb: "Payroll" },
  children: [
    {
      Component: CompanyPayrollLayout,
      handle: { flushContent: true },
      children: [
        { index: true, Component: CompanyPayrolls },
        {
          path: COMPANY_PAYROLL_PATH_SEGMENTS.SUMMARY,
          handle: { breadcrumb: "Summary" },
          Component: PayrollSummary,
        },
        {
          path: COMPANY_PAYROLL_PATH_SEGMENTS.DISPUTES,
          handle: { breadcrumb: "Disputes" },
          Component: CompanyDisputes,
        },
        {
          path: COMPANY_PAYROLL_PATH_SEGMENTS.PAY_SLIPS,
          handle: { breadcrumb: "Pay Slips" },
          Component: CompanyPaySlips,
        },
        {
          path: COMPANY_PAYROLL_PATH_SEGMENTS.PAYMENT_FILES,
          handle: { breadcrumb: "Payment Files" },
          Component: CompanyPaymentFiles,
        },
        {
          path: COMPANY_PAYROLL_PATH_SEGMENTS.FINAL_SETTLEMENTS,
          handle: { breadcrumb: "Final Settlement" },
          Component: CompanyFinalSettlements,
        },
      ],
    },
    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks the dynamic siblings in React Router's
    // route ranking, so there is no collision. ──────────────────────────────
    {
      path: COMPANY_PAYROLL_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Payroll Settings" },
      Component: CompanyPayrollSettingsPage,
    },
    {
      path: `${COMPANY_PAYROLL_PATH_SEGMENTS.FINAL_SETTLEMENTS}/:id`,
      handle: { breadcrumb: "Settlement Detail" },
      Component: FinalSettlementDetail,
    },
    {
      path: `${COMPANY_PAYROLL_PATH_SEGMENTS.DISPUTES}/:id`,
      handle: { breadcrumb: "Dispute Detail" },
      Component: CompanyDisputeDetail,
    },
    {
      path: COMPANY_PAYROLL_PATH_SEGMENTS.GENERATE,
      handle: { breadcrumb: "Generate Payroll" },
      Component: GeneratePayroll,
    },
    {
      path: `${COMPANY_PAYROLL_PATH_SEGMENTS.RUN}/:id`,
      handle: { breadcrumb: "Payroll Run" },
      Component: PayrollRun,
    },
    {
      path: `${COMPANY_PAYROLL_PATH_SEGMENTS.RUN}/:runId/items/:itemId`,
      handle: { breadcrumb: "Edit Item" },
      Component: EditPayrollRunItem,
    },
  ],
};

export const COMPANY_PAYROLLS_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_PAYROLL_PATH_SEGMENTS.ROOT)),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.PAYROLL.ROOT,
  SETTINGS_VTAB_POLICIES: MODULE_SETTINGS_PATHS.PAYROLL.VTAB_POLICIES,
  SETTINGS_VTAB_COUNTRY_RULES: MODULE_SETTINGS_PATHS.PAYROLL.VTAB_COUNTRY_RULES,
  SETTINGS_VTAB_BRANCH: MODULE_SETTINGS_PATHS.PAYROLL.VTAB_BRANCH,
  SETTINGS_VTAB_PAY_TYPES: MODULE_SETTINGS_PATHS.PAYROLL.VTAB_PAY_TYPES,
  SETTINGS_VTAB_PAY: MODULE_SETTINGS_PATHS.PAYROLL.VTAB_PAY,
  SETTINGS_VTAB_DEDUCTIONS: MODULE_SETTINGS_PATHS.PAYROLL.VTAB_DEDUCTIONS,
  SETTINGS_VTAB_BENEFITS: MODULE_SETTINGS_PATHS.PAYROLL.VTAB_BENEFITS,

  PAY_SLIPS: withCompanyPrefix(
    addSlashes(
      COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
      COMPANY_PAYROLL_PATH_SEGMENTS.PAY_SLIPS,
    ),
  ),
  PAYMENT_FILES: withCompanyPrefix(
    addSlashes(
      COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
      COMPANY_PAYROLL_PATH_SEGMENTS.PAYMENT_FILES,
    ),
  ),
  FINAL_SETTLEMENTS: withCompanyPrefix(
    addSlashes(
      COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
      COMPANY_PAYROLL_PATH_SEGMENTS.FINAL_SETTLEMENTS,
    ),
  ),
  DISPUTES: withCompanyPrefix(
    addSlashes(
      COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
      COMPANY_PAYROLL_PATH_SEGMENTS.DISPUTES,
    ),
  ),
  SUMMARY: withCompanyPrefix(
    addSlashes(
      COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
      COMPANY_PAYROLL_PATH_SEGMENTS.SUMMARY,
    ),
  ),
  GENERATE: withCompanyPrefix(
    addSlashes(
      COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
      COMPANY_PAYROLL_PATH_SEGMENTS.GENERATE,
    ),
  ),
  RUN: (id: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
        COMPANY_PAYROLL_PATH_SEGMENTS.RUN,
        String(id),
      ),
    ),
  RUN_ITEM: (runId: string | number, itemId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PAYROLL_PATH_SEGMENTS.ROOT,
        COMPANY_PAYROLL_PATH_SEGMENTS.RUN,
        String(runId),
        "items",
        String(itemId),
      ),
    ),
});
